import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { adminClient, isDefinitiveAdminAuthenticationFailure } from "../../lib/admin";
import { LogOut, LayoutDashboard, Inbox, Users, Shield, Star, MessageSquare, KeyRound, Mail, GraduationCap, Gift, ShieldCheck, FileEdit, Radio, PackageSearch, BarChart3, Activity, Store, LayoutGrid, SlidersHorizontal, Boxes, BellRing, CreditCard, Plug, CalendarClock, FileText, Scale, Bot } from "lucide-react";
import AdminOverview from "./sections/AdminOverview";
import AdminLeads from "./sections/AdminLeads";
import AdminUsers from "./sections/AdminUsers";
import AdminRoles from "./sections/AdminRoles";
import AdminReviews from "./sections/AdminReviews";
import AdminTickets from "./sections/AdminTickets";
import AdminEmail from "./sections/AdminEmail";
import AdminCourses from "./sections/AdminCourses";
import AdminPromotions from "./sections/AdminPromotions";
import AdminPriceGuard from "./sections/AdminPriceGuard";
import AdminUpdateCenter from "./sections/AdminUpdateCenter";
import AdminLive from "./sections/AdminLive";
import AdminGifts from "./sections/AdminGifts";
import AdminCatalog from "./sections/AdminCatalog";
import AdminCatalogHealth from "./sections/AdminCatalogHealth";
import AdminPromotionStudio from "./sections/AdminPromotionStudio";
import AdminContinuityConsole from "./sections/AdminContinuityConsole";
import AdminSecurity from "./sections/AdminSecurity";
import AdminAnalytics from "./sections/AdminAnalytics";
import AdminCJAutomation from "./sections/AdminCJAutomation";
import AdminStores from "./sections/AdminStores";
import AdminCollectionManager from "./sections/AdminCollectionManager";
import AdminPricingRules from "./sections/AdminPricingRules";
import AdminInventory from "./sections/AdminInventory";
import AdminInventoryAlerts from "./sections/AdminInventoryAlerts";
import AdminFeatureHub from "./sections/AdminFeatureHub";
import AdminMembership from "./sections/AdminMembership";
import AdminSellerIntegration from "./sections/AdminSellerIntegration";
import AdminSellerSources from "./sections/AdminSellerSources";
import AdminNeedsReview from "./sections/AdminNeedsReview";
import AdminScheduler from "./sections/AdminScheduler";
import AdminApplications from "./sections/AdminApplications";
import AdminLegal from "./sections/AdminLegal";
import AdminYeda from "./sections/AdminYeda";
import AdminVendors from "./sections/AdminVendors";

const SECTIONS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, perm: "view_dashboard", C: AdminOverview },
  { key: "leads", label: "Leads & Records", icon: Inbox, perm: "view_dashboard", C: AdminLeads },
  { key: "updates", label: "Update Center", icon: FileEdit, perm: "manage_settings", C: AdminUpdateCenter },
  { key: "live", label: "Live Commerce", icon: Radio, perm: "manage_sellers", C: AdminLive },
  { key: "analytics", label: "Shop the Feed", icon: BarChart3, perm: "manage_sellers", C: AdminAnalytics },
  { key: "gifts", label: "Gifts & Coins", icon: Gift, perm: "manage_promotions", C: AdminGifts },
  { key: "users", label: "Users & Roles", icon: Users, perm: "manage_users", C: AdminUsers },
  { key: "roles", label: "Permission Matrix", icon: Shield, perm: "manage_roles", C: AdminRoles },
  { key: "priceguard", label: "Price Guard", icon: ShieldCheck, perm: "approve_products", C: AdminPriceGuard },
  { key: "needsreview", label: "Needs Review", icon: Inbox, perm: "approve_products", C: AdminNeedsReview },
  { key: "catalog", label: "Catalog & Rotation", icon: PackageSearch, perm: "manage_settings", C: AdminCatalog },
  { key: "cataloghealth", label: "Catalog Health", icon: Activity, perm: "manage_settings", C: AdminCatalogHealth },
  { key: "promotionstudio", label: "Promotion Studio", icon: Gift, perm: "manage_promotions", C: AdminPromotionStudio },
  { key: "continuity", label: "Business Continuity", icon: CalendarClock, perm: "manage_sellers", C: AdminContinuityConsole },
  { key: "collectionmgr", label: "Collection Display", icon: LayoutGrid, perm: "manage_settings", C: AdminCollectionManager },
  { key: "pricingrules", label: "Pricing Rules", icon: SlidersHorizontal, perm: "manage_settings", C: AdminPricingRules },
  { key: "inventory", label: "Live Inventory", icon: Boxes, perm: "manage_settings", C: AdminInventory },
  { key: "inventoryalerts", label: "Inventory Alerts", icon: BellRing, perm: "manage_settings", C: AdminInventoryAlerts },
  { key: "cjautomation", label: "CJ Automation", icon: Activity, perm: "manage_settings", C: AdminCJAutomation },
  { key: "scheduler", label: "Weekly Scheduler", icon: CalendarClock, perm: "manage_settings", C: AdminScheduler },
  { key: "featurehub", label: "Feature Hub", icon: LayoutGrid, perm: "manage_settings", C: AdminFeatureHub },
  { key: "membership", label: "Memberships", icon: CreditCard, perm: "manage_settings", C: AdminMembership },
  { key: "applications", label: "Applications", icon: FileText, perm: "manage_sellers", C: AdminApplications },
  { key: "vendors", label: "Seller Center", icon: Store, perm: "manage_sellers", C: AdminVendors },
  { key: "legal", label: "Legal Center", icon: Scale, perm: "manage_legal", C: AdminLegal },
  { key: "yeda", label: "YEDA Guide", icon: Bot, perm: "manage_settings", C: AdminYeda },
  { key: "sellerintegration", label: "Seller Integration", icon: Plug, perm: "manage_settings", C: AdminSellerIntegration },
  { key: "sellersources", label: "Seller Sources", icon: PackageSearch, perm: "manage_settings", C: AdminSellerSources },
  { key: "aistores", label: "Store Creator", icon: Store, perm: "manage_settings", C: AdminStores },
  { key: "reviews", label: "Reviews", icon: Star, perm: "manage_reviews", C: AdminReviews },
  { key: "tickets", label: "Support Tickets", icon: MessageSquare, perm: "manage_tickets", C: AdminTickets },
  { key: "courses", label: "Learning Center", icon: GraduationCap, perm: "manage_courses", C: AdminCourses },
  { key: "promotions", label: "Spin & Save", icon: Gift, perm: "manage_promotions", C: AdminPromotions },
  { key: "email", label: "Email Center", icon: Mail, perm: "manage_promotions", C: AdminEmail },
  { key: "security", label: "Security", icon: KeyRound, perm: "view_dashboard", C: AdminSecurity },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [active, setActive] = useState(searchParams.get("section") || "overview");
  const [sessionError, setSessionError] = useState("");
  const [sessionAttempt, setSessionAttempt] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("taven_admin_token");
    if (!token) { navigate("/admin/login"); return; }
    let activeRequest = true;
    setSessionError("");
    adminClient()
      .get("/admin/whoami")
      .then(({ data }) => {
        if (!activeRequest) return;
        setMe(data);
        setSessionError("");
      })
      .catch((error) => {
        // Definitive auth failures (401 / invalid|expired|revoked) already revoke + redirect
        // inside adminClient(). Transient failures (network, timeout, 5xx, 429, 403) must
        // PRESERVE the session and offer a safe retry — never bounce the admin to login.
        if (!activeRequest || isDefinitiveAdminAuthenticationFailure(error)) return;
        setSessionError(
          "We could not verify the admin session right now. Your session was preserved; retry safely."
        );
      });
    return () => { activeRequest = false; };
  }, [navigate, sessionAttempt]);

  const logout = () => { localStorage.removeItem("taven_admin_token"); navigate("/admin/login"); };

  if (!me && sessionError) {
    return (
      <main data-testid="ecos-session-retry" role="alert" className="min-h-screen flex flex-col items-center justify-center gap-4 bg-taven-gray/40 px-6 text-center">
        <h1 className="text-2xl font-black font-display text-taven-black">Admin session verification unavailable</h1>
        <p className="text-sm text-black/70 max-w-md">{sessionError}</p>
        <button
          type="button"
          data-testid="ecos-session-retry-btn"
          onClick={() => { setSessionError(""); setSessionAttempt((c) => c + 1); }}
          className="bg-taven-black text-white px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-taven-gold hover:text-black"
        >
          Retry session verification
        </button>
      </main>
    );
  }

  if (!me) return <div className="min-h-screen flex items-center justify-center bg-taven-gray/40" data-testid="admin-loading">Loading…</div>;

  const perms = new Set(me.permissions);
  const visible = SECTIONS.filter((s) => perms.has(s.perm));
  const Active = (visible.find((s) => s.key === active) || visible[0])?.C || AdminOverview;

  return (
    <div className="min-h-screen bg-taven-gray/30" data-testid="admin-dashboard">
      <header className="bg-taven-black text-white flex items-center justify-between px-6 h-16">
        <div><span className="text-lg font-black font-display">TAVEN Admin</span><span className="text-xs text-taven-gold ml-3 uppercase tracking-widest">Control Center</span></div>
        <div className="flex items-center gap-4">
          {(me.roles?.includes("super_admin") || perms.has("command_center.read")) && (
            <button
              onClick={() => navigate("/command-center")}
              data-testid="admin-open-command-center"
              className="hidden sm:flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-taven-gold text-black px-3 py-1.5 hover:bg-white transition-colors"
            >
              <LayoutGrid className="w-4 h-4" /> Command Center
            </button>
          )}
          <span className="text-xs text-white/70 hidden sm:block">{me.name} · <span className="text-taven-gold">{me.roles.join(", ")}</span></span>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-1.5 text-sm font-bold hover:text-taven-gold"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </header>

      <div className="grid md:grid-cols-[220px_1fr]">
        <nav className="bg-white border-r border-black/10 md:min-h-[calc(100vh-4rem)] p-3 flex md:flex-col gap-1 overflow-x-auto">
          {visible.map((s) => (
            <button key={s.key} onClick={() => setActive(s.key)} data-testid={`admin-nav-${s.key}`}
              className={`flex items-center gap-2.5 px-4 py-3 text-sm font-bold whitespace-nowrap transition-colors ${active === s.key ? "bg-taven-black text-white" : "hover:bg-taven-gray"}`}>
              <s.icon className="w-4 h-4" /> {s.label}
            </button>
          ))}
        </nav>
        <main className="p-6 max-w-full overflow-x-hidden">
          <Active />
        </main>
      </div>
    </div>
  );
}
